import { Grid } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { default as MUITextField } from '@material-ui/core/TextField'
import SendIcon from '@material-ui/icons/Send'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import { DatePicker } from '@material-ui/pickers'
import jsonExport from 'jsonexport/dist'
import { get, range } from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import {
  ArrayField,
  Button,
  CheckboxGroupInput,
  ChipField,
  Datagrid,
  DateField,
  DateInput,
  downloadCSV,
  email as emailValidator,
  Filter,
  FunctionField,
  SelectInput,
  SingleFieldList,
  TextField,
  TextInput,
  useGetIdentity,
  useNotify,
} from 'react-admin'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { pluralize } from 'shared/helpers/utils'

import { exportStatements } from '../../api/jobs'
import {
  DESTINATION,
  LOAD_STOP_WINDOW_TYPES,
  StatementStatusChoices,
  StatementTypeChoices,
  STATUS_DETAIL_CHOICES,
} from '../../helpers/constants'
import { captureExceptionIfProduction, getLoadStop, getLocalizedStopTime } from '../../helpers/utils'
import { CustomReferenceField } from '../Common/CustomReferenceFields'
import { ListActionsLargeExport } from '../Common/ListActions'
import { TotalCashAdvancePayments } from '../Common/Statements'
import { LabeledTypographyField, LocalizedStopDateTimeField } from '../Fields'
import CTList from '../List'

const StatementFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
    <SelectInput label="Type" source="type" choices={StatementTypeChoices} />
    <SelectInput label="Status" source="status" choices={StatementStatusChoices} />
    <CheckboxGroupInput label="Job detail" source="job__status_detail" choices={STATUS_DETAIL_CHOICES} />
    <DateInput label="Date processed after" source="date_processed__after" />
    <DateInput label="Date processed before" source="date_processed__before" />
    <DateInput label="Delivery date after" source="delivery_date__after" />
    <DateInput label="Delivery date before" source="delivery_date__before" />
  </Filter>
)

const exporter = (statements: any) => {
  const data = statements.map((statement: any) => {
    const { id, status, date_created, date_updated, is_editable, job, ...statementForExport } = statement
    statementForExport.line_items = statementForExport.line_items.map((lineItem: any) => {
      const { id, statement, description, date_created, date_updated, ...lineItemForExport } = lineItem
      return lineItemForExport
    })
    statementForExport.fee_items = statementForExport.fee_items.map((feeItem: any) => {
      const { id, statement, date_created, date_updated, ...feeItemForExport } = feeItem
      return feeItemForExport
    })
    if (statementForExport.date_processed !== null && statementForExport.date_processed !== undefined) {
      statementForExport.date_processed = DateTime.fromISO(statementForExport.date_processed).toLocaleString(
        DateTime.DATETIME_FULL
      )
    }
    let statementJob
    if (statement.job !== null) {
      const { human_id, status_detail, load } = statement.job
      const { source, source_human_id } = load
      const dropoffDate = getLocalizedStopTime(load, DESTINATION, LOAD_STOP_WINDOW_TYPES.start)
      statementJob = {
        human_id: human_id,
        status_detail: status_detail,
        load: {
          source: source,
          source_human_id: source_human_id,
        },
        dropoff_date: dropoffDate,
      }
    } else {
      statementJob = null
    }
    return {
      ...statementForExport,
      job: statementJob,
    }
  })
  jsonExport(
    data,
    {
      headers: [
        'date_processed',
        'job.dropoff_date',
        'job.human_id',
        'job.load.source',
        'job.load.source_human_id',
        'job.status_detail',
        'team',
        'description',
        'line_items.amount',
        'line_items.type',
        'subtotal',
        'fee_items.amount',
        'fee_items.description',
        'lease_fee_percent',
        'service_fee_percent',
        'lease_fee',
        'service_fee',
        'cash_advance_payments.amount',
        'cash_advance_payments.fee',
        'total',
      ],
    },
    (err, csv) => {
      downloadCSV(csv, 'statements')
    }
  )
}

const StatementPanel = ({ record }: any) => {
  return (
    <Grid container direction="row" alignItems="flex-start" justifyContent="flex-start" spacing={6}>
      <Grid item>
        <LabeledTypographyField label="Type" value={record.type} variant="body2" />
      </Grid>
      <Grid item>
        <LabeledTypographyField label="Status" value={record.status} variant="body2" />
      </Grid>
    </Grid>
  )
}

const ExportToEmailButton = () => {
  const { identity, loading: identityLoading } = useGetIdentity()
  const [email, setEmail] = useState('')
  const emailInputErrorMessage = get(emailValidator('Email is not valid.')(email, {}), 'message', '')

  const [isDialogOpen, setDialogOpen] = useState(false)

  const now = DateTime.now()
  const toMonthYear = (date: any) => [date.month, date.year]
  const [timeFrameToggle, setTimeFrameToggle] = useState('current_month')
  const [selectedMonths, setSelectedMonths] = useState([toMonthYear(now)])

  const [isCustomDatePickerOpen, setCustomDatePickerOpen] = useState(false)
  const [customStartDate, setCustomStartDate] = useState<any>()
  const [customEndDate, setCustomEndDate] = useState<any>()

  const [dateType, setDateType] = useState('statement_processed')

  const [isSubmitLoading, setSubmitLoading] = useState(false)

  const notify = useNotify()

  useEffect(() => {
    // default to sending reports to the logged-in user's email
    if (!email && !identityLoading && identity) {
      setEmail(get(identity, 'email', ''))
    }
  }, [email, identity, identityLoading])

  useEffect(() => {
    const dateRangeToMonthsArray = (startDate: any, endDate: any) => {
      // given a start and end date, return an array of (month, year) tuples for every month between the two dates (inclusive)
      const months = []
      let currentMonth = startDate
      while (currentMonth <= endDate) {
        months.push(toMonthYear(currentMonth))
        currentMonth = currentMonth.plus({ months: 1 })
      }
      return months
    }

    // any time custom start/end date changes, update selected months
    if (timeFrameToggle === 'custom' && customStartDate && customEndDate) {
      setSelectedMonths(dateRangeToMonthsArray(customStartDate, customEndDate))
    }
  }, [customStartDate, customEndDate, timeFrameToggle])

  return (
    <>
      <Button onClick={() => setDialogOpen(true)} label="Export to Email" disabled={identityLoading}>
        <SendIcon />
      </Button>
      <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Statements Export</DialogTitle>
        <DialogContent>
          {isSubmitLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <DialogContentText>Where should these reports be sent?</DialogContentText>
                <MUITextField
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={identityLoading}
                  error={!!emailInputErrorMessage}
                  helperText={emailInputErrorMessage}
                  required
                  fullWidth
                />
              </Box>
              <Box mt={2}>
                <DialogContentText>Time Frame</DialogContentText>
                <ToggleButtonGroup
                  value={timeFrameToggle}
                  color="primary"
                  onChange={(_, newValue) => {
                    setTimeFrameToggle(newValue)

                    if (newValue === 'current_month') {
                      setCustomDatePickerOpen(false)
                      setSelectedMonths([toMonthYear(now)])
                    } else if (newValue === 'last_month') {
                      setCustomDatePickerOpen(false)
                      setSelectedMonths([toMonthYear(now.minus({ months: 1 }))])
                    } else if (newValue === 'year_to_date') {
                      setCustomDatePickerOpen(false)
                      const currentTuple = toMonthYear(now)
                      const currentMonth = currentTuple[0]
                      const currentYear = currentTuple[1]
                      setSelectedMonths(range(1, currentMonth + 1).map((month) => [month, currentYear]))
                    } else if (newValue === 'custom') {
                      setCustomStartDate(now.minus({ months: 12 })) // default to 12 months ago
                      setCustomEndDate(now)
                      setCustomDatePickerOpen(true)
                    }
                  }}
                  orientation="horizontal"
                  exclusive
                >
                  <ToggleButton value="current_month">Current Month</ToggleButton>
                  <ToggleButton value="last_month">Last Month</ToggleButton>
                  <ToggleButton value="year_to_date">Year-to-Date</ToggleButton>
                  <ToggleButton value="custom">Custom</ToggleButton>
                </ToggleButtonGroup>
                {isCustomDatePickerOpen && (
                  <Box mt={2}>
                    <DatePicker
                      inputVariant="outlined"
                      openTo="month"
                      views={['year', 'month']}
                      label="Start Month"
                      value={customStartDate}
                      onChange={(date) => setCustomStartDate(date)}
                      minDate={DateTime.fromISO('2020-01-01')}
                      minDateMessage="Earliest Start is January 2020"
                      maxDate={customEndDate}
                      maxDateMessage="Start Date must be before End Date"
                      disableFuture
                    />
                    <Box mt={2}>
                      <DatePicker
                        inputVariant="outlined"
                        openTo="month"
                        views={['year', 'month']}
                        label="End Month (inclusive)"
                        value={customEndDate}
                        onChange={(date) => setCustomEndDate(date)}
                        minDate={customStartDate}
                        minDateMessage="End Month must be after Start Month"
                        disableFuture
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              <Box mt={2}>
                <DialogContentText>
                  Should we filter by the date the statement was processed, or by the job delivery date?
                </DialogContentText>
                <ToggleButtonGroup
                  value={dateType}
                  color="primary"
                  onChange={(_, newValue) => setDateType(newValue)}
                  orientation="horizontal"
                  exclusive
                >
                  <ToggleButton value="statement_processed">Statement Processed Date</ToggleButton>
                  <ToggleButton value="job_delivery">Job Delivery Date</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box mt={2}>
                <DialogContentText align="center">
                  <Box sx={{ fontStyle: 'italic' }}>
                    Note: It may take up to 20 minutes to generate your reports. You'll receive an email from
                    noreply@cloudtrucks.com when they are ready.
                  </Box>
                </DialogContentText>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} label="Cancel" disabled={isSubmitLoading} />
          <Button
            variant="contained"
            disabled={
              identityLoading ||
              !!emailInputErrorMessage ||
              !email ||
              !dateType ||
              !timeFrameToggle ||
              isSubmitLoading
            }
            onClick={async () => {
              try {
                setSubmitLoading(true)
                const isOK = await exportStatements(email, selectedMonths, dateType)
                if (isOK) {
                  setDialogOpen(false)
                  setSubmitLoading(false)
                  notify('Generating your report! It will take a few minutes.', {
                    type: 'success',
                  })
                } else {
                  throw new Error('Export failed')
                }
              } catch (error) {
                setDialogOpen(false)
                setSubmitLoading(false)
                notify(`Oops, something went wrong: ${JSON.stringify(error)}`, { type: 'error' })
                captureExceptionIfProduction(error)
              }
            }}
            label={`Export ${pluralize(selectedMonths.length, 'month')}`}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

const StatementList = (props: any) => {
  usePageTitle('Statements')
  return (
    <CTList
      bulkActionButtons={false}
      filters={<StatementFilter />}
      actions={<ListActionsLargeExport customActions={<ExportToEmailButton />} />}
      exporter={exporter}
      {...props}
    >
      <Datagrid optimized expand={<StatementPanel />} rowClick="show">
        <DateField source="date_processed" options={{ timeZoneName: 'short' }} />
        <FunctionField
          label="Delivery date"
          sortBy="job__load__dest_delivery_date"
          render={(record: any) => {
            if (!record.job || !record.job.load || !record.job.load.stops) {
              return null
            }
            const destStop = getLoadStop(record.job.load, DESTINATION)
            return destStop ? <LocalizedStopDateTimeField record={destStop} source="date_start" /> : null
          }}
        />
        <TextField sortable={false} source="job.human_id" label="Job ID" />
        <TextField sortable={false} source="job.load.source" label="Source" />
        <TextField sortable={false} source="job.load.source_human_id" label="Source ID" />
        <TextField sortable={false} source="job.status_detail" label="Job status detail" />
        <CustomReferenceField
          source="team"
          sortable={false}
          label="Team"
          baseUrl="/teams"
          representationFunction={(team: any) => team.name}
        />
        <ArrayField source="line_items" sortable={false}>
          <SingleFieldList linkType={false}>
            <ChipField source="amount" />
          </SingleFieldList>
        </ArrayField>
        <ArrayField source="fee_items" sortable={false}>
          <SingleFieldList linkType={false}>
            <ChipField source="amount" />
          </SingleFieldList>
        </ArrayField>
        <TextField source="subtotal" sortable={false} />
        <TextField source="lease_fee" sortable={false} />
        <TextField source="service_fee" sortable={false} />
        <TextField source="dispatch_service_fee" sortable={false} />
        <TotalCashAdvancePayments label="Total cash advance payments" />
        <TextField source="total" />
      </Datagrid>
    </CTList>
  )
}

export default StatementList
